import type MarkdownIt from "markdown-it";

/* Formulas in an answer as markdown-it tokens: `$…$` and `\(…\)` in a sentence, `$$…$$`
   displayed in a sentence or on lines of its own, `\[…\]` displayed on lines of its own only —
   in a sentence it is Markdown's escaped bracket, as in "arr\[0\]". Each token carries its TeX
   and the source as written (`meta.source`), which shows when KaTeX can't draw it.

   A price stays text, by Pandoc's rule: an opening `$` has a character right after it, and the
   next `$` closes it only with a character right before it and no digit after — so "$5 and
   $10" and "$5-$10" never pair up. Ceiling: a postfix "10$" after a "$5", and `$HOME/$PATH`
   outside code, still pair. A block whose closing line hasn't come is a paragraph, so a
   formula being written shows its source until it closes. */

export type Formula = { tex: string; display: boolean; source: string };
const CLOSERS: Record<string, string> = { $$: "$$", $: "$", "\\(": "\\)", "\\[": "\\]" };
const SPACE = /\s/;
const DIGIT = /\d/;

export default function math(md: MarkdownIt.MarkdownIt) {
  md.inline.ruler.before("escape", "math", (state, silent) => {
    const found = inline(state.src, state.pos, state.posMax);
    if (!found) return false;
    if (!silent) state.push("math", "", 0).meta = found.math;
    state.pos = found.end;
    return true;
  });
  md.block.ruler.before("fence", "math_block", block, { alt: ["paragraph", "reference", "blockquote", "list"] });
}

function inline(src: string, start: number, max: number): { math: Formula; end: number } | null {
  const opener = ["$$", "$", "\\("].find((mark) => src.startsWith(mark, start));
  if (!opener) return null;
  const closer = CLOSERS[opener];
  const from = start + opener.length;
  let close = src.indexOf(closer, from);
  while (close > 0 && opener === "$" && src[close - 1] === "\\") close = src.indexOf(closer, close + 1); // \$ is a dollar
  const end = close + closer.length;
  if (close < 0 || end > max || !src.slice(from, close).trim()) return null;
  if (opener === "$" && (SPACE.test(src[from]) || SPACE.test(src[close - 1]) || DIGIT.test(src[end] ?? ""))) return null;
  return { math: { tex: src.slice(from, close), display: opener === "$$", source: src.slice(start, end) }, end };
}

/** The text with each formula in it blanked to spaces, the same length: for reading the marks
    around formulas without the ones inside (a `[0, 1)` interval is no link). */
export function blankFormulas(text: string): string {
  let out = "";
  for (let at = 0; at < text.length; ) {
    if (text.startsWith("\\$", at)) {
      out += "\\$"; // an escaped dollar opens nothing
      at += 2;
      continue;
    }
    const found = inline(text, at, text.length);
    out += found ? " ".repeat(found.end - at) : text[at];
    at = found ? found.end : at + 1;
  }
  return out;
}

/** Lines that open with `$$` or `\[` and end, on that line or a later one before a blank line,
    with the closer. */
function block(state: MarkdownIt.StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
  if (state.sCount[startLine] - state.blkIndent >= 4) return false; // indented code
  const text = (line: number) => state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line]).trimEnd();
  const opener = ["$$", "\\["].find((mark) => text(startLine).startsWith(mark));
  if (!opener) return false;
  const closer = CLOSERS[opener];
  let last = startLine;
  while (!(text(last).endsWith(closer) && (last > startLine || text(last).length >= opener.length + closer.length))) {
    if (++last >= endLine || state.isEmpty(last)) return false;
  }
  const source = state.getLines(startLine, last + 1, state.sCount[startLine], false).trim();
  const tex = source.slice(opener.length, -closer.length);
  if (!tex.trim()) return false;
  if (silent) return true;
  const token = state.push("math_block", "", 0);
  token.block = true;
  token.map = [startLine, last + 1];
  token.meta = { tex, display: true, source } satisfies Formula;
  state.line = last + 1;
  return true;
}
